import { getGlobalData } from '@/lib/utils';
import { getTrans } from '@/services/global/translations.interface';

interface Language {
	dictionary?: { [translationKey: string]: string };
}

interface GlobalWindow extends Window {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	CKEDITOR_TRANSLATIONS?: { [locale: string]: Language };
}

/**
 * Sets the Editor UI language. Instead of extending the Editor UI language we override it with our own.
 * @see https://ckeditor.com/docs/ckeditor5/latest/framework/deep-dive/ui/localization.html#using-the-windowckeditor_translations-object
 */
const instantiateUITranslation = () => {
	const globalWindow = window as GlobalWindow;
	const translations = (globalWindow.CKEDITOR_TRANSLATIONS = globalWindow.CKEDITOR_TRANSLATIONS || {});

	const { locale } = getGlobalData('language');
	const language = translations?.[locale] ?? (translations[locale] = {});

	if (!language.dictionary) {
		setLanguageDictionary(language);
	}
};

const setLanguageDictionary = (language: Language) => {
	const lang = getTrans();

	// These are all required translations for Editor. However, we are currently utilizing only a subset of them.
	// As a result, many of these translations haven't been set in the translation repository.
	// This setup aims to facilitate the identification of missing translations in the future.
	language.dictionary = {
		...language.dictionary,
		'%0 of %1': lang.get('editor.of'),
		'Align center': lang.get('editor.align_center'),
		'Align left': lang.get('editor.align_left'),
		'Align right': lang.get('editor.align_right'),
		Aquamarine: lang.get('editor.aquamarine'),
		'Attach File': lang.get('editor.attach_file'),
		Black: lang.get('editor.black'),
		'Block quote': lang.get('editor.block_quote'),
		Blue: lang.get('editor.blue'),
		Bold: lang.get('editor.bold'),
		'Break text': lang.get('editor.break_text'),
		'Bulleted List': lang.get('editor.bulleted_list'),
		Cancel: lang.get('editor.cancel'),
		'Caption for image: %0': lang.get('editor.caption_for_image'),
		'Caption for the image': lang.get('editor.caption_for_the_image'),
		'Centered image': lang.get('editor.centered_image'),
		'Change image text alternative': lang.get('editor.change_image_text_alternative'),
		'Characters: %0': lang.get('editor.characters'),
		'Choose heading': lang.get('editor.choose_heading'),
		Code: lang.get('editor.code'),
		Column: lang.get('editor.column'),
		'Decrease indent': lang.get('editor.decrease_indent'),
		'Delete column': lang.get('editor.delete_column'),
		'Delete row': lang.get('editor.delete_row'),
		'Dim grey': lang.get('editor.dim_grey'),
		Downloadable: lang.get('editor.downloadable'),
		'Dropdown toolbar': lang.get('editor.dropdown_toolbar'),
		'Edit block': lang.get('editor.edit_block'),
		'Edit link': lang.get('editor.edit_link'),
		'Editor block content toolbar': lang.get('editor.editor_block_content_toolbar'),
		'Editor contextual toolbar': lang.get('editor.editor_contextual_toolbar'),
		'Editor editing area: %0': lang.get('editor.editor_editing_area'),
		'Editor toolbar': lang.get('editor.editor_toolbar'),
		'Enter image caption': lang.get('editor.enter_image_caption'),
		'Full size image': lang.get('editor.full_size_image'),
		Green: lang.get('editor.green'),
		Grey: lang.get('editor.grey'),
		'Header column': lang.get('editor.header_column'),
		'Header row': lang.get('editor.header_row'),
		Heading: lang.get('editor.heading'),
		'Heading 1': lang.get('editor.heading_1'),
		'Heading 2': lang.get('editor.heading_2'),
		'Heading 3': lang.get('editor.heading_3'),
		'Heading 4': lang.get('editor.heading_4'),
		'Heading 5': lang.get('editor.heading_5'),
		'Heading 6': lang.get('editor.heading_6'),
		'Image resize list': lang.get('editor.image_resize_list'),
		'image widget': lang.get('editor.image_widget'),
		'In line': lang.get('editor.in_line'),
		'Increase indent': lang.get('editor.increase_indent'),
		'Insert column left': lang.get('editor.insert_column_left'),
		'Insert column right': lang.get('editor.insert_column_right'),
		'Insert media': lang.get('editor.insert_media'),
		'Insert row above': lang.get('editor.insert_row_above'),
		'Insert row below': lang.get('editor.insert_row_below'),
		'Insert table': lang.get('editor.insert_table'),
		Italic: lang.get('editor.italic'),
		Justify: lang.get('editor.justify'),
		'Left aligned image': lang.get('editor.left_aligned_image'),
		'Light blue': lang.get('editor.light_blue'),
		'Light green': lang.get('editor.light_green'),
		'Light grey': lang.get('editor.light_grey'),
		Link: lang.get('editor.link'),
		'Link image': lang.get('editor.link_image'),
		'Link URL': lang.get('editor.link_url'),
		'Media URL': lang.get('editor.media_url'),
		'media widget': lang.get('editor.media_widget'),
		'Merge cell down': lang.get('editor.merge_cell_down'),
		'Merge cell left': lang.get('editor.merge_cell_left'),
		'Merge cell right': lang.get('editor.merge_cell_right'),
		'Merge cell up': lang.get('editor.merge_cell_up'),
		'Merge cells': lang.get('editor.merge_cells'),
		Next: lang.get('editor.next'),
		'Numbered List': lang.get('editor.numbered_list'),
		'Open in a new tab': lang.get('editor.open_in_a_new_tab'),
		'Open link in new tab': lang.get('editor.open_link_in_new_tab'),
		'Open media in new tab': lang.get('editor.open_media_in_new_tab'),
		Orange: lang.get('editor.orange'),
		Original: lang.get('editor.original'),
		Paragraph: lang.get('editor.paragraph'),
		'Paste the media URL in the input.': lang.get('editor.paste_the_media_url_in_the_input'),
		Previous: lang.get('editor.previous'),
		Purple: lang.get('editor.purple'),
		Red: lang.get('editor.red'),
		Redo: lang.get('editor.redo'),
		'Resize image': lang.get('editor.resize_image'),
		'Resize image to %0': lang.get('editor.resize_image_to'),
		'Resize image to the original size': lang.get('editor.resize_image_to_the_original_size'),
		'Rich Text Editor': lang.get('editor.rich_text_editor'),
		'Right aligned image': lang.get('editor.right_aligned_image'),
		Row: lang.get('editor.row'),
		Save: lang.get('editor.save'),
		'Select column': lang.get('editor.select_column'),
		'Select row': lang.get('editor.select_row'),
		'Show more items': lang.get('editor.show_more_items'),
		'Side image': lang.get('editor.side_image'),
		'Split cell horizontally': lang.get('editor.split_cell_horizontally'),
		'Split cell vertically': lang.get('editor.split_cell_vertically'),
		Strikethrough: lang.get('editor.strikethrough'),
		Subscript: lang.get('editor.subscript'),
		Superscript: lang.get('editor.superscript'),
		'Table toolbar': lang.get('editor.table_toolbar'),
		'Text alignment': lang.get('editor.text_alignment'),
		'Text alignment toolbar': lang.get('editor.text_alignment_toolbar'),
		'Text alternative': lang.get('editor.text_alternative'),
		'The URL must not be empty.': lang.get('editor.the_url_must_not_be_empty'),
		'This link has no URL': lang.get('editor.this_link_has_no_url'),
		'This media URL is not supported.': lang.get('editor.this_media_url_is_not_supported'),
		'Tip: Paste the URL into the content to embed faster.': lang.get(
			'editor.tip_paste_the_url_into_the_content_to_embed_faster'
		),
		'Toggle caption off': lang.get('editor.toggle_caption_off'),
		'Toggle caption on': lang.get('editor.toggle_caption_on'),
		Turquoise: lang.get('editor.turquoise'),
		Underline: lang.get('editor.underline'),
		Undo: lang.get('editor.undo'),
		Unlink: lang.get('editor.unlink'),
		White: lang.get('editor.white'),
		'Words: %0': lang.get('editor.words'),
		'Wrap text': lang.get('editor.wrap_text'),
		Yellow: lang.get('editor.yellow'),
	};
};

export { instantiateUITranslation };
